import { yupResolver } from '@hookform/resolvers/yup';
import { BenefitDocumentType, BenefitStatus, PlanMarket } from '@zorro/clients';
import { useForm, useMonolithQuery } from '@zorro/web/ui-utils';
import Decimal from 'decimal.js';
import { useEffect } from 'react';

import {
  EnrollmentConfirmationFormFields,
  enrollmentConfirmationSchema,
  isCoverageStartingTodayOrLater,
} from './EnrollmentConfirmationUtils';

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
};

export const useEnrollmentConfirmationForm = ({
  employeeId,
  onboardingPeriodId,
}: Props) => {
  const { data: onboardingPeriod, isLoading: isLoadingOnboardingPeriod } =
    useMonolithQuery({
      method: 'onboardingPeriodsControllerFindOne',
      params: [employeeId, onboardingPeriodId],
    });
  const { data: benefits, isLoading: isLoadingBenefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const majorMedicalPlan = benefits?.majorMedicalBenefit?.plan;

  const benefitStatus = benefits?.majorMedicalBenefit?.status;
  const isBenefitStatusEnrolled =
    benefitStatus === BenefitStatus.AWAITING_PAYMENT ||
    benefitStatus === BenefitStatus.ENROLLMENT_CONFIRMED ||
    benefitStatus === BenefitStatus.ACTIVE ||
    benefitStatus === BenefitStatus.ENDED;
  const isBenefitNotSubmitted =
    !benefitStatus || benefitStatus === BenefitStatus.IN_CART;
  // The confirmation email is held back while a plan awaits payment, so the
  // form offers it again as it would for a first confirmation.
  const isEmailPossiblySent =
    benefitStatus === BenefitStatus.ENROLLMENT_CONFIRMED ||
    benefitStatus === BenefitStatus.ACTIVE ||
    benefitStatus === BenefitStatus.ENDED;

  const allBenefitDocuments = onboardingPeriod?.benefitDocuments ?? [];

  const previousConfirmationEvidence = allBenefitDocuments.filter(
    ({ type }) => type === BenefitDocumentType.CONFIRMATION
  );

  const enrollmentConfirmationForm = useForm<EnrollmentConfirmationFormFields>({
    mode: 'all',
    defaultValues: {
      planMarket:
        !benefits?.majorMedicalBenefit?.plan?.planMarket ||
        benefits?.majorMedicalBenefit?.plan?.planMarket ===
          PlanMarket.BOTH_MARKETS
          ? undefined
          : benefits?.majorMedicalBenefit?.plan?.planMarket,
      confirmationEvidence: previousConfirmationEvidence,
      memberId: benefits?.majorMedicalBenefit?.memberId ?? undefined,
      noMemberAvailable:
        isBenefitStatusEnrolled && !benefits?.majorMedicalBenefit?.memberId,
      shouldSendEnrollmentConfirmationEmail:
        !onboardingPeriod ||
        isBenefitNotSubmitted ||
        (!isEmailPossiblySent &&
          isCoverageStartingTodayOrLater(onboardingPeriod.effectiveFrom)),
      noEvidenceAvailable:
        isBenefitStatusEnrolled && previousConfirmationEvidence.length === 0,
      employeeMonthlyContribution:
        majorMedicalPlan?.employeeMonthlyContribution ?? undefined,
      premium: majorMedicalPlan?.premium,
    },
    resolver: yupResolver(enrollmentConfirmationSchema),
  });

  const premium = enrollmentConfirmationForm.watch('premium');

  const allowance =
    onboardingPeriod?.allowance ??
    majorMedicalPlan?.employerMonthlyContribution ??
    0;

  const employerMonthlyContribution = Math.min(allowance, premium);

  useEffect(() => {
    if (!premium) {
      return;
    }

    const employeeMonthlyContribution = Math.max(
      new Decimal(premium).sub(employerMonthlyContribution).toNumber(),
      0
    );

    if (
      employeeMonthlyContribution ===
      enrollmentConfirmationForm.getValues().employeeMonthlyContribution
    ) {
      return;
    }

    enrollmentConfirmationForm.setValue(
      'employeeMonthlyContribution',
      employeeMonthlyContribution
    );
  }, [employerMonthlyContribution, premium, enrollmentConfirmationForm]);

  return {
    enrollmentConfirmationForm,
    employerMonthlyContribution,
    allowance,
    isLoading: isLoadingOnboardingPeriod || isLoadingBenefits,
  };
};
